/**
 * AttentionRowActions (KAN-828)
 *
 * Правая часть строки блока внимания: выбор менеджера (если вид его даёт,
 * AttentionViewConfig.assignManager) и кнопки закрытия строки. Пока менеджер
 * сохраняется, кнопки закрытия недоступны; пока идёт закрытие, недоступен
 * выбор менеджера.
 * KAN-960: `nextContact` (клиент строки) добавляет кнопку "Следующий контакт".
 * KAN-965: элементы стоят в одном ряду строки (AttentionRow), контролы низкие;
 * "Следующий контакт" и "Работа идёт" иконками с подписью в title и aria-label.
 */
import type { AttentionItem } from '@/services/api/dashboard'
import { NextContactAction } from '@/components/clients/NextContactAction'
import type { NextContactTarget } from '@/lib/nextContact'
import { AttentionActionButtons } from './AttentionActionButtons'
import { AttentionManagerAction } from './AttentionManagerAction'
import type { AttentionAction } from './useAttentionActions'
import { useAttentionManager } from './useAttentionManager'

interface AttentionRowActionsProps {
  item: Pick<AttentionItem, 'client_id' | 'manager'>
  actions: readonly AttentionAction[]
  /** Показывать выбор менеджера (флаг вида блока). */
  assignManager: boolean
  /** Идёт запрос закрытия этой строки. */
  closing: boolean
  onAction: (action: AttentionAction) => void
  /** KAN-852: label of the "done" button (useAttentionDone().label). */
  doneLabel?: string
  /** KAN-960: клиент строки для кнопки "Следующий контакт"; нет: кнопки нет. */
  nextContact?: NextContactTarget
}

export function AttentionRowActions({
  item,
  actions,
  assignManager,
  closing,
  onAction,
  doneLabel,
  nextContact,
}: AttentionRowActionsProps) {
  const manager = useAttentionManager(item)
  // KAN-965: the elements are items of one row (AttentionRow, attention-row-actions);
  // low controls, "Следующий контакт" is an icon (label in title and aria-label).
  return (
    <>
      {assignManager && <AttentionManagerAction item={item} manager={manager} disabled={closing} />}
      <AttentionActionButtons
        actions={actions}
        disabled={closing || manager.pending}
        onAction={onAction}
        doneLabel={doneLabel}
        dense
      />
      {nextContact && (
        <NextContactAction
          target={nextContact}
          disabled={closing || manager.pending}
          compact
          className="h-8 w-8 p-0"
        />
      )}
    </>
  )
}
